import type { MRT_RowSelectionState, MRT_TableInstance, MRT_TableState } from '@talentmesh/table';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useShadcnTable } from '@/Hooks';
import { ShortlistedProfile } from '../../../api/DTO/ShortlistedProfile';
import { useShortlistedListContext } from './ShortlistedListContext';
import useColumns from './useColumns/useColumns';

type ShortlistedTableState = Partial<MRT_TableState<ShortlistedProfile>>;
type LocalStorageTableState = { tableVersion?: number } & ShortlistedTableState;
type MRTStateMap = Map<string, LocalStorageTableState>;
type ShortlistedTableReturnContext = MRT_TableInstance<ShortlistedProfile> & {
    rowSelection: MRT_RowSelectionState;
};

const currentTableVersion = 2;
const mrtStateKey = 'aiSearchShortlistedMrtState';

export const ShortlistedTableContext = createContext<ShortlistedTableReturnContext | undefined>(undefined);

export const useShortlistedTableContextProvider = (): ShortlistedTableReturnContext => {
    const { assessmentId } = useParams();
    const columns = useColumns();
    const { data, isLoading, isValidating } = useShortlistedListContext();
    // manage row selection manually for listnening to changes
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const initialState = useMemo<LocalStorageTableState | undefined>(() => {
        const mrtStateStr = localStorage.getItem(mrtStateKey);

        if (mrtStateStr) {
            const mrtStateMap: MRTStateMap = new Map(JSON.parse(mrtStateStr));
            const tableState = mrtStateMap.get(assessmentId!);

            if (tableState?.tableVersion === currentTableVersion) {
                return mrtStateMap.get(assessmentId!) || undefined;
            }
            localStorage.removeItem(mrtStateKey);
        }

        return undefined;
    }, [assessmentId]);

    const table = useShadcnTable({
        columns,
        data: data?.data,
        state: { showSkeletons: isLoading, isLoading: false, rowSelection },
        isValidating,
        initialState,
        enableSorting: false,
        onRowSelectionChange: setRowSelection,
    });

    useEffect(() => {
        return () => {
            let mrtStateMap: MRTStateMap = new Map();
            const mrtStateStr = localStorage.getItem(mrtStateKey);

            if (mrtStateStr) {
                mrtStateMap = new Map(JSON.parse(mrtStateStr));
            }

            const tableState: LocalStorageTableState = {
                ...table.getState(),
                rowSelection: undefined,
                tableVersion: currentTableVersion,
            };
            mrtStateMap.set(assessmentId!, tableState);

            localStorage.setItem(mrtStateKey, JSON.stringify(Array.from(mrtStateMap.entries())));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- persist latest table state on unmount only
    }, []);

    return { ...table, rowSelection };
};

export const useShortlistedTableContext = () => {
    const context = useContext(ShortlistedTableContext);
    if (!context) {
        throw new Error('useShortlistedTableContext must be used within its provider');
    }
    return context;
};
